import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { toast } from "sonner";
import {
  Clock,
  Divide,
  SkipForward,
  Timer,
  ChevronRight,
  ArrowLeft,
  Loader2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";

const QuizPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const [quizData, setQuizData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lifelines, setLifelines] = useState({
    fifty_fifty: true,
    skip: true,
    extra_time: true,
  });
  const [eliminatedOptions, setEliminatedOptions] = useState([]);
  const selectedAnswersRef = useRef([]);

  // Get difficulty from navigation state or default to "mixed"
  const difficulty = location.state?.difficulty || "mixed";

  // Start quiz on mount
  useEffect(() => {
    startQuiz();
  }, []);

  // Timer countdown
  useEffect(() => {
    if (!quizData || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [quizData]);

  const startQuiz = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quiz/start`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ subject: "income_tax", difficulty }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setQuizData(data);
        const initialAnswers = new Array(data.questions.length).fill(-1);
        setSelectedAnswers(initialAnswers);
        selectedAnswersRef.current = initialAnswers;
        setLifelines(data.lifelines);
        setTimeLeft(data.time_limit);
      } else {
        let errDetail = null;
        try { errDetail = (await response.json()).detail; } catch {}
        if (errDetail?.code === "DIFFICULTY_EXHAUSTED") {
          const DIFF_LABEL = { easy: "Basic", medium: "Moderate", hard: "Advanced", expert: "Expert", super_hard: "Super Hard" };
          const diff = DIFF_LABEL[errDetail.difficulty] || errDetail.difficulty || "This";
          toast.error(`🎯 You've answered all ${diff} questions! Try a different difficulty or challenge a friend.`, { duration: 6000 });
          navigate("/dashboard");
        } else if (errDetail?.code === "ALL_QUESTIONS_SEEN") {
          toast.success("🏆 You've mastered all questions! Come back tomorrow or challenge a friend!", { duration: 6000 });
          navigate("/challenges");
        } else if (errDetail?.code === "SUPER_HARD_LOCKED") {
          toast.error(`🔒 Super Hard unlocks after you answer all ${errDetail.required} regular questions. You've seen ${errDetail.seen} so far!`, { duration: 5000 });
          navigate("/dashboard");
        } else {
          toast.error(typeof errDetail === "string" ? errDetail : "Failed to start quiz");
          navigate("/dashboard");
        }
      }
    } catch (error) {
      toast.error("Connection error");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAnswer = (optionIndex) => {
    if (eliminatedOptions.includes(optionIndex)) return;

    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestion] = optionIndex;
    setSelectedAnswers(newAnswers);
    selectedAnswersRef.current = newAnswers;
  };

  const handleNext = () => {
    if (currentQuestion < quizData.questions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
      setEliminatedOptions([]);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
      setEliminatedOptions([]);
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quiz/submit`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            quiz_id: quizData.quiz_id,
            answers: selectedAnswersRef.current,
            time_taken: (quizData?.time_limit || 300) - timeLeft,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        // Trigger confetti for good scores
        if (result.score >= 60) {
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
          });
        }
        navigate(`/results/${quizData.quiz_id}`, { state: { result } });
      } else {
        let errMsg = "Failed to submit quiz";
        try {
          const errData = await response.json();
          if (errData?.detail) errMsg = errData.detail;
        } catch {}
        toast.error(errMsg);
        // If session expired or not found, go back to dashboard
        if (response.status === 400 || response.status === 404) {
          setTimeout(() => navigate("/dashboard"), 2500);
        }
      }
    } catch (error) {
      toast.error("Connection error — please check your internet and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLifeline = async (type) => {
    if (!lifelines[type]) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quiz/lifeline`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            quiz_id: quizData.quiz_id,
            question_index: currentQuestion,
            lifeline_type: type,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        setLifelines((prev) => ({ ...prev, [type]: false }));

        if (type === "fifty_fifty" && result.eliminate) {
          setEliminatedOptions(result.eliminate);
          toast.success("Two wrong options eliminated!");
        } else if (type === "skip") {
          // Mark as skipped and move to next
          const newAnswers = [...selectedAnswers];
          newAnswers[currentQuestion] = -2; // -2 means skipped
          setSelectedAnswers(newAnswers);
          selectedAnswersRef.current = newAnswers;
          toast.success("Question skipped!");
          handleNext();
        } else if (type === "extra_time") {
          setTimeLeft((prev) => prev + result.extra_seconds);
          toast.success(`+${result.extra_seconds} seconds added!`);
        }
      }
    } catch (error) {
      toast.error("Failed to use lifeline");
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getTimerClass = () => {
    if (timeLeft <= 30) return "timer-danger";
    if (timeLeft <= 60) return "timer-warning";
    return "timer-normal";
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#f8faef", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Nunito', sans-serif" }}>
        <div style={{ textAlign: "center", maxWidth: 360, padding: "0 20px" }}>
          <div style={{ width: 80, height: 80, background: "linear-gradient(135deg,#10b981,#059669)", borderRadius: 24, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 8px 32px rgba(16,185,129,0.3)" }}>
            <Loader2 size={36} color="white" className="animate-spin" />
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 900, color: "#1e293b", marginBottom: 8 }}>Generating Your Quiz...</h2>
          <p style={{ color: "#64748b", fontSize: 14, marginBottom: 20 }}>Pulling 15 questions from our bank of 1,000</p>
          <div style={{ background: "white", borderRadius: 16, padding: "14px 18px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: 12, textAlign: "left" }}>
            <span style={{ fontSize: 24 }}>💡</span>
            <div>
              <p style={{ fontWeight: 700, color: "#1e293b", fontSize: 13, margin: 0 }}>Did you know?</p>
              <p style={{ color: "#64748b", fontSize: 12, margin: 0, marginTop: 2 }}>Section 80C allows deductions up to ₹1.5 lakhs under the Old Regime!</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!quizData) return null;

  const question = quizData.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / quizData.questions.length) * 100;

  return (
    <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: "#f8faef", fontFamily: "'Nunito', sans-serif", overflow: "hidden" }}>

      {/* ── Sticky Header ── */}
      <div style={{ flexShrink: 0, background: "white", borderBottom: "1px solid #e5e7eb", padding: "10px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
        <button
          onClick={() => { if (window.confirm("Are you sure? Your progress will be lost.")) navigate("/dashboard"); }}
          style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: "#64748b", fontSize: 14, fontWeight: 700, fontFamily: "'Nunito', sans-serif", padding: "6px 10px", borderRadius: 8 }}
          data-testid="exit-quiz-btn"
        >
          <ArrowLeft size={16} /> Exit
        </button>

        {/* Progress info */}
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b" }}>
            Question {currentQuestion + 1} of {quizData.questions.length}
          </div>
          <div style={{ width: 160, height: 4, background: "#e5e7eb", borderRadius: 4, marginTop: 4, overflow: "hidden" }}>
            <div style={{ width: `${progress}%`, height: "100%", background: "linear-gradient(90deg,#10b981,#059669)", borderRadius: 4, transition: "width 0.4s ease" }} />
          </div>
        </div>

        {/* Timer */}
        <div style={{
          display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 100, fontWeight: 800, fontSize: 15,
          background: timeLeft <= 30 ? "#ef4444" : timeLeft <= 60 ? "#f59e0b" : "#059669",
          color: "white",
        }} data-testid="quiz-timer">
          <Clock size={15} />
          {formatTime(timeLeft)}
        </div>
      </div>

      {/* ── Scrollable Content ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px 8px" }}>
        <div style={{ maxWidth: 680, margin: "0 auto" }}>

          {/* Question Card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
            >
              <div style={{ background: "white", borderRadius: 24, padding: "24px 28px", boxShadow: "0 4px 24px rgba(0,0,0,0.08)", marginBottom: 14, border: "1px solid #f1f5f9" }}>
                {/* Topic badge */}
                <span style={{ display: "inline-block", padding: "3px 12px", background: "#dcfce7", color: "#166534", borderRadius: 100, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 14 }}>
                  Income Tax
                </span>
                {/* Question text */}
                <h2 style={{ fontSize: 18, fontWeight: 800, color: "#1e293b", lineHeight: 1.45, marginBottom: 20, margin: "0 0 20px" }}>
                  {question.question}
                </h2>
                {/* Options */}
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {question.options.map((option, index) => {
                    const isEliminated = eliminatedOptions.includes(index);
                    const isSelected = selectedAnswers[currentQuestion] === index;
                    return (
                      <button
                        key={index}
                        onClick={() => handleSelectAnswer(index)}
                        disabled={isEliminated}
                        data-testid={`option-${index}`}
                        style={{
                          width: "100%", textAlign: "left", padding: "12px 16px",
                          borderRadius: 14, border: isSelected ? "2px solid #10b981" : "1.5px solid #e2e8f0",
                          background: isEliminated ? "#f8fafc" : isSelected ? "#f0fdf4" : "white",
                          cursor: isEliminated ? "not-allowed" : "pointer",
                          opacity: isEliminated ? 0.4 : 1,
                          display: "flex", alignItems: "center", gap: 12,
                          fontFamily: "'Nunito', sans-serif", fontWeight: 600, fontSize: 15, color: "#1e293b",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <span style={{ width: 32, height: 32, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, fontWeight: 800, fontSize: 14, background: isSelected ? "#10b981" : "#f1f5f9", color: isSelected ? "white" : "#64748b", transition: "all 0.15s" }}>
                          {String.fromCharCode(65 + index)}
                        </span>
                        <span style={{ flex: 1, lineHeight: 1.4 }}>{option}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Lifelines */}
          <div style={{ display: "flex", justifyContent: "center", gap: 16, marginBottom: 8 }}>
            {[
              { key: "fifty_fifty", icon: <Divide size={18} />, label: "50:50", testId: "lifeline-fifty-fifty" },
              { key: "skip",        icon: <SkipForward size={18} />, label: "Skip", testId: "lifeline-skip" },
              { key: "extra_time",  icon: <Timer size={18} />, label: "+30s", testId: "lifeline-extra-time" },
            ].map(l => (
              <button
                key={l.key}
                onClick={() => handleLifeline(l.key)}
                disabled={!lifelines[l.key]}
                data-testid={l.testId}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                  padding: "10px 20px", borderRadius: 14, border: "1.5px solid #e2e8f0",
                  background: lifelines[l.key] ? "white" : "#f8fafc",
                  cursor: lifelines[l.key] ? "pointer" : "not-allowed",
                  opacity: lifelines[l.key] ? 1 : 0.45,
                  fontFamily: "'Nunito', sans-serif", color: "#475569", fontWeight: 700, fontSize: 12,
                  boxShadow: lifelines[l.key] ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
                }}
              >
                {l.icon}{l.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Sticky Bottom Bar: Nav + Dots ── */}
      <div style={{ flexShrink: 0, background: "white", borderTop: "1px solid #f1f5f9", padding: "10px 20px 12px", boxShadow: "0 -2px 12px rgba(0,0,0,0.05)" }}>
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          {/* Question dots */}
          <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
            {quizData.questions.map((_, index) => (
              <button
                key={index}
                onClick={() => { setCurrentQuestion(index); setEliminatedOptions([]); }}
                data-testid={`question-nav-${index}`}
                style={{
                  width: 30, height: 30, borderRadius: "50%", fontSize: 12, fontWeight: 700,
                  border: "none", cursor: "pointer", transition: "all 0.15s",
                  background: currentQuestion === index ? "#059669" : selectedAnswers[index] !== -1 ? "#dcfce7" : "#f1f5f9",
                  color: currentQuestion === index ? "white" : selectedAnswers[index] !== -1 ? "#166534" : "#94a3b8",
                }}
              >
                {index + 1}
              </button>
            ))}
          </div>
          {/* Navigation buttons */}
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              data-testid="prev-question-btn"
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "10px 22px", borderRadius: 100,
                border: "1.5px solid #e2e8f0", background: "white", cursor: currentQuestion === 0 ? "not-allowed" : "pointer",
                opacity: currentQuestion === 0 ? 0.4 : 1, fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: 14, color: "#475569",
              }}
            >
              <ArrowLeft size={16} /> Previous
            </button>

            {currentQuestion === quizData.questions.length - 1 ? (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="submit-quiz-btn"
                style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "10px 28px", borderRadius: 100,
                  background: submitting ? "#6ee7b7" : "linear-gradient(135deg,#059669,#0d9488)",
                  border: "none", cursor: submitting ? "not-allowed" : "pointer",
                  fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 15, color: "white",
                  boxShadow: "0 4px 16px rgba(5,150,105,0.3)",
                }}
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                {submitting ? "Submitting..." : "Submit Quiz"} {!submitting && <ChevronRight size={16} />}
              </button>
            ) : (
              <button
                onClick={handleNext}
                data-testid="next-question-btn"
                style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "10px 28px", borderRadius: 100,
                  background: "linear-gradient(135deg,#059669,#0d9488)",
                  border: "none", cursor: "pointer",
                  fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 15, color: "white",
                  boxShadow: "0 4px 16px rgba(5,150,105,0.3)",
                }}
              >
                Next <ChevronRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizPage;
